import { useEffect, useId, useState } from "react";
import { UsersRound } from "lucide-react";

import { Button } from "../atoms/Button";
import { Drawer } from "./Drawer";
import { Input } from "../atoms/Input";
import { PRODUCT_MODULES } from "../../constants/productModules";
import type {
  AuthorizationBusinessUnit,
  AuthorizationBusinessUnitGroupMapping,
  AuthorizationExternalGroup,
  AuthorizationRole,
  AuthorizationRoleGroupMapping,
  BuScopeModuleMapping,
  ExternalGroupCreatePayload,
  ExternalGroupUpdatePayload,
  GroupType
} from "../../types/authorizationAdmin";

type ExternalGroupDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly editingGroup?: AuthorizationExternalGroup | null;
  /** All groups (active and inactive) for duplicate display-name check (case-insensitive, trim). */
  readonly existingExternalGroups?: readonly AuthorizationExternalGroup[];
  readonly roles: readonly AuthorizationRole[];
  readonly businessUnits: readonly AuthorizationBusinessUnit[];
  readonly roleGroupMappings: readonly AuthorizationRoleGroupMapping[];
  readonly buGroupMappings: readonly AuthorizationBusinessUnitGroupMapping[];
  readonly buScopeModuleMappings: readonly BuScopeModuleMapping[];
  readonly onCreate: (payload: ExternalGroupCreatePayload) => Promise<void>;
  readonly onUpdate: (groupId: string, payload: ExternalGroupUpdatePayload) => Promise<void>;
};

export const ExternalGroupDrawer = ({
  isOpen,
  onClose,
  editingGroup,
  existingExternalGroups = [],
  roles,
  businessUnits,
  roleGroupMappings,
  buGroupMappings,
  buScopeModuleMappings,
  onCreate,
  onUpdate
}: ExternalGroupDrawerProps) => {
  const [provider, setProvider] = useState("OKTA");
  const [externalGroupId, setExternalGroupId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupType, setGroupType] = useState<GroupType>("ROLE");
  const [isActive, setIsActive] = useState(true);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [selectedBuIds, setSelectedBuIds] = useState<string[]>([]);
  const [selectedModuleCodes, setSelectedModuleCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (editingGroup) {
      setProvider(editingGroup.provider);
      setExternalGroupId(editingGroup.external_group_id);
      setGroupName(editingGroup.group_name);
      setGroupType(editingGroup.group_type);
      setIsActive(editingGroup.is_active);
      const activeRoleLinks = roleGroupMappings.filter(
        (m) => m.external_group_ref_id === editingGroup.id && m.is_active
      );
      const activeBuLinks = buGroupMappings.filter(
        (m) => m.external_group_ref_id === editingGroup.id && m.is_active
      );
      setSelectedRoleIds(activeRoleLinks.map((m) => m.decision_role_id));
      setSelectedBuIds(activeBuLinks.map((m) => m.business_unit_id));
      const activeModules = buScopeModuleMappings.filter(
        (m) => m.external_group_ref_id === editingGroup.id && m.is_active
      );
      setSelectedModuleCodes(activeModules.map((m) => m.module_code));
    } else {
      setProvider("OKTA");
      setExternalGroupId("");
      setGroupName("");
      setGroupType("ROLE");
      setIsActive(true);
      setSelectedRoleIds([]);
      setSelectedBuIds([]);
      setSelectedModuleCodes([]);
    }
  }, [isOpen, editingGroup, roleGroupMappings, buGroupMappings, buScopeModuleMappings]);

  const isEdit = Boolean(editingGroup);
  const createRoleRadioGroupName = useId();

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) => (prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]));
  };

  const toggleBu = (buId: string) => {
    setSelectedBuIds((prev) => (prev.includes(buId) ? prev.filter((id) => id !== buId) : [...prev, buId]));
  };

  const toggleModuleCode = (code: string) => {
    setSelectedModuleCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit External Group" : "Add External Group"}
      headerIcon={<UsersRound className="h-5 w-5" />}
    >
      <div className="space-y-4 transition-all duration-200 ease-in-out">
        <div className="space-y-1.5">
          <label htmlFor="eg-provider" className="form-label">
            Provider <span className="form-label-asterisk">*</span>
          </label>
          <Input id="eg-provider" value={provider} onChange={(e) => setProvider(e.target.value)} />
        </div>
        {!isEdit ? (
          <>
            <div className="space-y-1.5">
              <label htmlFor="eg-ext-id" className="form-label">
                External group ID <span className="form-label-asterisk">*</span>
              </label>
              <Input
                id="eg-ext-id"
                value={externalGroupId}
                onChange={(e) => setExternalGroupId(e.target.value)}
                placeholder="Stable directory / Okta object ID or group key"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="eg-type" className="form-label">
                Group type <span className="form-label-asterisk">*</span>
              </label>
              <select
                id="eg-type"
                value={groupType}
                onChange={(e) => {
                  const next = e.target.value as GroupType;
                  setGroupType(next);
                  setSelectedRoleIds([]);
                  setSelectedBuIds([]);
                  setSelectedModuleCodes([]);
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm transition-all duration-200 ease-in-out"
              >
                <option value="ROLE">Decision role group (maps to GREEN / AMBER / RED)</option>
                <option value="BU_SCOPE">Business unit scope group</option>
              </select>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-1.5">
              <label htmlFor="eg-ext-id-edit" className="form-label">
                External group ID <span className="form-label-asterisk">*</span>
              </label>
              <Input
                id="eg-ext-id-edit"
                value={externalGroupId}
                onChange={(e) => setExternalGroupId(e.target.value)}
                placeholder="Stable directory / Okta object ID or group key"
              />
            </div>
            <div className="space-y-1.5">
              <span className="form-label">Group type</span>
              <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {groupType}
              </p>
            </div>
          </>
        )}
        <div className="space-y-1.5">
          <label htmlFor="eg-name" className="form-label">
            Display name <span className="form-label-asterisk">*</span>
          </label>
          <Input id="eg-name" value={groupName} onChange={(e) => setGroupName(e.target.value)} />
        </div>

        {!isEdit && groupType === "ROLE" && (
          <div className="space-y-2">
            <span className="form-label">
              Decision role <span className="form-label-asterisk">*</span>
            </span>
            <p className="text-xs text-gray-600">
              Select the decision tier granted by membership in this group (one role).
            </p>
            <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
              {roles
                .filter((r) => r.is_active)
                .map((role) => (
                  <label
                    key={role.id}
                    className="flex cursor-pointer items-center gap-2 text-sm text-gray-800 transition-all duration-200 ease-in-out"
                  >
                    <input
                      type="radio"
                      name={createRoleRadioGroupName}
                      value={role.id}
                      checked={selectedRoleIds[0] === role.id}
                      onChange={() => setSelectedRoleIds([role.id])}
                      className="border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      {role.code} — {role.name}
                    </span>
                  </label>
                ))}
            </div>
          </div>
        )}

        {!isEdit && groupType === "BU_SCOPE" && (
          <div className="space-y-2">
            <span className="form-label">
              Business units <span className="form-label-asterisk">*</span>
            </span>
            <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
              {businessUnits
                .filter((b) => b.is_active)
                .map((bu) => (
                  <label
                    key={bu.id}
                    className="flex cursor-pointer items-center gap-2 text-sm text-gray-800 transition-all duration-200 ease-in-out"
                  >
                    <input
                      type="checkbox"
                      checked={selectedBuIds.includes(bu.id)}
                      onChange={() => toggleBu(bu.id)}
                      className="rounded border-gray-300"
                    />
                    <span>
                      {bu.code} — {bu.name}
                    </span>
                </label>
              ))}
            </div>
            <div className="space-y-2 pt-2">
              <span className="form-label">Product modules</span>
              <p className="text-xs text-gray-600">Optional. Links BU scope groups to product areas (configuration only).</p>
              <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
                {PRODUCT_MODULES.map((mod) => (
                  <label
                    key={mod.code}
                    className="flex cursor-pointer items-center gap-2 text-sm text-gray-800 transition-all duration-200 ease-in-out"
                  >
                    <input
                      type="checkbox"
                      checked={selectedModuleCodes.includes(mod.code)}
                      onChange={() => toggleModuleCode(mod.code)}
                      className="rounded border-gray-300"
                    />
                    <span>
                      {mod.label} ({mod.code})
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {isEdit && editingGroup?.group_type === "ROLE" && (
          <div className="space-y-2">
            <span className="form-label">
              Decision roles <span className="form-label-asterisk">*</span>
            </span>
            <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className="flex cursor-pointer items-center gap-2 text-sm text-gray-800 transition-all duration-200 ease-in-out"
                >
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.includes(role.id)}
                    onChange={() => toggleRole(role.id)}
                    className="rounded border-gray-300"
                    disabled={!role.is_active && !selectedRoleIds.includes(role.id)}
                  />
                  <span className={!role.is_active ? "text-gray-400" : ""}>
                    {role.code} — {role.name}
                    {!role.is_active ? " (inactive)" : ""}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {isEdit && editingGroup?.group_type === "BU_SCOPE" && (
          <div className="space-y-2">
            <span className="form-label">
              Business units <span className="form-label-asterisk">*</span>
            </span>
            <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
              {businessUnits.map((bu) => (
                <label
                  key={bu.id}
                  className="flex cursor-pointer items-center gap-2 text-sm text-gray-800 transition-all duration-200 ease-in-out"
                >
                  <input
                    type="checkbox"
                    checked={selectedBuIds.includes(bu.id)}
                    onChange={() => toggleBu(bu.id)}
                    className="rounded border-gray-300"
                    disabled={!bu.is_active && !selectedBuIds.includes(bu.id)}
                  />
                  <span className={!bu.is_active ? "text-gray-400" : ""}>
                    {bu.code} — {bu.name}
                    {!bu.is_active ? " (inactive)" : ""}
                  </span>
                </label>
              ))}
            </div>
            <div className="space-y-2 pt-2">
              <span className="form-label">Product modules</span>
              <p className="text-xs text-gray-600">Optional. Saved with this BU scope group.</p>
              <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
                {PRODUCT_MODULES.map((mod) => (
                  <label
                    key={mod.code}
                    className="flex cursor-pointer items-center gap-2 text-sm text-gray-800 transition-all duration-200 ease-in-out"
                  >
                    <input
                      type="checkbox"
                      checked={selectedModuleCodes.includes(mod.code)}
                      onChange={() => toggleModuleCode(mod.code)}
                      className="rounded border-gray-300"
                    />
                    <span>
                      {mod.label} ({mod.code})
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-gray-700 transition-all duration-200 ease-in-out">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Active
          </label>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={isSaving}
            onClick={async () => {
              if (!groupName.trim()) {
                setError("Display name is required.");
                return;
              }
              const nameKey = groupName.trim().toLowerCase();
              const nameConflict = existingExternalGroups.some(
                (g) =>
                  g.group_name.trim().toLowerCase() === nameKey &&
                  (!editingGroup || g.id !== editingGroup.id)
              );
              if (nameConflict) {
                setError("A group with this display name already exists.");
                return;
              }
              const gt = isEdit ? editingGroup!.group_type : groupType;
              if (gt === "ROLE" && selectedRoleIds.length === 0) {
                setError("Select a decision role.");
                return;
              }
              if (gt === "BU_SCOPE" && selectedBuIds.length === 0) {
                setError("Select at least one business unit.");
                return;
              }
              setError(null);
              setIsSaving(true);
              try {
                if (editingGroup) {
                  if (!externalGroupId.trim()) {
                    setError("External group ID is required.");
                    setIsSaving(false);
                    return;
                  }
                  const extIdTrim = externalGroupId.trim();
                  const idConflict = existingExternalGroups.some(
                    (g) =>
                      g.external_group_id.trim() === extIdTrim && g.id !== editingGroup.id
                  );
                  if (idConflict) {
                    setError("Another group already uses this external group ID.");
                    setIsSaving(false);
                    return;
                  }
                  const updatePayload: ExternalGroupUpdatePayload = {
                    provider: provider.trim() || undefined,
                    external_group_id: extIdTrim,
                    group_name: groupName.trim(),
                    is_active: isActive,
                    ...(editingGroup.group_type === "ROLE"
                      ? { decision_role_ids: [...selectedRoleIds] }
                      : { business_unit_ids: [...selectedBuIds], product_module_codes: [...selectedModuleCodes] })
                  };
                  await onUpdate(editingGroup.id, updatePayload);
                } else {
                  if (!externalGroupId.trim()) {
                    setError("External group ID is required.");
                    setIsSaving(false);
                    return;
                  }
                  const createPayload: ExternalGroupCreatePayload = {
                    provider: provider.trim() || "OKTA",
                    external_group_id: externalGroupId.trim(),
                    group_name: groupName.trim(),
                    group_type: groupType,
                    ...(groupType === "ROLE"
                      ? { decision_role_ids: [...selectedRoleIds] }
                      : {
                          business_unit_ids: [...selectedBuIds],
                          ...(selectedModuleCodes.length > 0 ? { product_module_codes: [...selectedModuleCodes] } : {})
                        })
                  };
                  await onCreate(createPayload);
                }
                onClose();
              } catch (err) {
                setError(err instanceof Error ? err.message : "Unable to save external group.");
              } finally {
                setIsSaving(false);
              }
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
